// Feed cards — one card per feed item. Two variants: release + feature
// announcement. Each card is a compact summary; its full detail (media,
// items, meta) renders as a full-page view on the Timeline via the exported
// *DetailView components. Read-only there — editor/content-author
// CRUD lives on the Releases/Features detail pages, reached via the inline
// Edit affordance.
import { useState } from "react";
import { Link, useLocation } from "wouter";
import type {
  EnrichedFeature,
  Release,
  ReleaseBuild,
  ReleaseRollup,
} from "@release-hub/shared";
import {
  useCreateReleaseFromBuilds,
  useIgnoreBuilds,
  useRefreshStaging,
} from "@/api/mutations";
import { useWorkspaces } from "@/api/queries";
import { useEffectiveRole } from "@/auth/role";
import { cn } from "@/lib/cn";
import { composeFeature, composeRelease } from "@/lib/exportHtml";
import { featureTier } from "@/lib/featureWorkspaces";
import { extractLinks, type ExtractedLink } from "@/lib/links";
import { fmtDate, fmtDateTime, fmtRelative } from "@/lib/format";
import { markdownToReact } from "@/lib/markdown";
import { decodeProjectedGa, formatProjectedGa } from "@/lib/projectedGa";
import { effectiveReleaseProminence } from "@/lib/releaseProminence";
import { releaseUrl, featureUrl, releaseDrawerUrl, featureDrawerUrl } from "@/lib/url";
import { Loading, Spinner } from "@/ui/spinner";
import { CopyExportButtons } from "./CopyExportButtons";
import { CopyLinkButton } from "./CopyLinkButton";
import { EnabledWorkspacesLine } from "./EnabledWorkspacesLine";
import { FlagChip } from "./FlagChip";
import { ItemList } from "./ItemList";
import { MediaGallery } from "./MediaGallery";
import { SideDrawer } from "./SideDrawer";
import { StateChip } from "./StateChip";
import { WorkspaceHoverWrap } from "./WorkspaceHoverWrap";

interface ReleaseCardProps {
  data: Release;
  /** Suppress the in-card date when the caller already shows one — e.g. the
   *  Timeline's left date rail. Rail-less lists (Unreleased, drafts) leave it
   *  on so the date isn't lost. */
  hideDate?: boolean;
  /** Open the detail view. Omitted for static previews (the editor's live
   *  preview), which drops the Details affordances. */
  onOpen?: () => void;
}

function platformLabel(build: ReleaseBuild): string {
  if (build.surface === "web") return "Web";
  if (build.platform === "ios") return "iOS";
  if (build.platform === "android") return "Android";
  return "Mobile";
}

/** Renders an editor-authored narrative with its formatting intact — line
 *  breaks, blank-line paragraph gaps, and `-`/`*` bullet lists — instead of
 *  collapsing everything onto one wrapped line. See lib/markdown.tsx. */
function NarrativeBody({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "space-y-2 leading-relaxed",
        "[&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 [&_li]:marker:text-text-tertiary",
        "[&_strong]:font-semibold",
        "[&_code]:rounded [&_code]:bg-border/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em]",
        className,
      )}
    >
      {markdownToReact(text)}
    </div>
  );
}

/** Display facts shared by the release card and its detail panel. */
function releaseSummary(data: Release) {
  const builds = data.builds ?? [];
  const primary = builds[0];
  // Planned release: scheduled ahead of any GitHub build. Surface comes from
  // the declared planned_surface instead of the (empty) builds.
  const isPlanned = builds.length === 0 && !!data.planned_surface;
  const isMobile =
    builds.some((b) => b.surface === "mobile") ||
    (isPlanned && data.planned_surface === "mobile");
  const isMinor = effectiveReleaseProminence(data) === "minor";
  // Tight grouping: paired iOS+Android (and single) releases collapse to
  // ONE chip. Expanding platform-level detail is the exception, not the
  // default — see hub-critique.md open note.
  const oneChipLabel = (() => {
    if (isPlanned) {
      return data.planned_surface === "mobile" ? "Mobile release" : "Web release";
    }
    if (builds.length === 0) return null;
    if (builds.length === 1 && primary) return `${platformLabel(primary)} release`;
    if (isMobile) {
      const platforms = Array.from(
        new Set(
          builds
            .filter((b) => b.surface === "mobile")
            .map((b) => b.platform)
            .filter((p): p is "ios" | "android" => p === "ios" || p === "android"),
        ),
      );
      if (platforms.length >= 2) return "Mobile release";
      if (platforms.length === 1) return `${platforms[0] === "ios" ? "iOS" : "Android"} release`;
      return "Mobile release";
    }
    return "Web release";
  })();
  // Displayed date = manual override, else earliest publish across the
  // wrapped builds (server pre-sorts, but keep the min here defensively).
  const displayDate =
    data.release_date_override ??
    builds.map((b) => b.published_at).sort()[0] ??
    null;
  // Prefer the editor's curated title. Mobile releases are ~always a paired
  // iOS + Android build of the same version, so "Mobile release" says
  // nothing the platform pill doesn't — title them with just the version
  // (dropping the +build suffix). Web falls back to its "Web release" label.
  const primaryTag = primary?.tag_name ?? null;
  const mobileVersion = isMobile && primaryTag ? primaryTag.split("+")[0] : null;
  const titleText =
    data.title?.trim() || mobileVersion || oneChipLabel || "Release";
  // Short platform word for the header pill ("Web" / "Mobile" / "iOS") so it
  // reads as a category marker instead of echoing the title verbatim.
  const platformShort = oneChipLabel?.replace(/ release$/i, "") ?? null;
  const githubLinks: ExtractedLink[] = builds
    .filter((b) => b.html_url)
    .map((b) => ({
      href: b.html_url!,
      label: builds.length > 1 ? `GitHub · ${platformLabel(b)}` : "GitHub",
    }));
  return {
    builds,
    primary,
    isPlanned,
    isMobile,
    isMinor,
    oneChipLabel,
    displayDate,
    titleText,
    platformShort,
    githubLinks,
  };
}

export function ReleaseCard({
  data,
  hideDate = false,
  onOpen,
}: ReleaseCardProps) {
  const { canEditContent } = useEffectiveRole();
  const {
    builds,
    primary,
    isPlanned,
    isMobile,
    isMinor,
    oneChipLabel,
    displayDate,
    titleText,
    platformShort,
    githubLinks,
  } = releaseSummary(data);

  // Minor releases render muted + compact. They still open to the full
  // detail view — just don't visually compete with a GA-worthy release on
  // the Timeline. See SE6 in hub-critique.md.
  return (
    <CardShell isMinor={isMinor}>
      {/* Signal row — what shipped: platform, version tag, build count.
          Author chrome + actions move to the footer below the narrative. */}
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-text-secondary">
        {oneChipLabel && (
          <span
            className={
              isMinor
                ? "rounded-full bg-border/50 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary"
                : isMobile
                  ? "rounded-full bg-mobile-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-mobile"
                  : "rounded-full bg-nightly-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-nightly"
            }
          >
            {isMinor ? "Patch" : platformShort}
          </span>
        )}
        {primary?.tag_name && (
          <span
            className={
              isMinor
                ? "font-mono text-[11px] text-text-tertiary"
                : "font-mono text-xs"
            }
          >
            {primary.tag_name}
          </span>
        )}
        {builds.length > 1 && (
          <span className="text-text-secondary">
            · {builds.length} builds
          </span>
        )}
      </div>
      {/* Serif title, like the feature-launch cards. Scaled down + muted
          for minor "Patch" releases so they don't shout on the Timeline. */}
      <CardTitle
        onClick={onOpen}
        className={cn(
          "mt-2 block",
          isMinor ? "text-lg text-text-secondary" : "text-2xl",
        )}
      >
        {titleText}
      </CardTitle>
      {data.narrative && (
        <NarrativeBody
          text={data.narrative}
          className={
            isMinor
              ? "mt-1.5 text-[12.5px] text-text-tertiary"
              : "mt-1.5 text-text-secondary"
          }
        />
      )}
      {!isMinor && (
        <CardLinksAndChips
          links={githubLinks}
          chips={[
            data.items && data.items.length > 0 && (
              <InfoChip key="items" onClick={onOpen} title="Open the full change list">
                {data.items.length} {data.items.length === 1 ? "change" : "changes"}
              </InfoChip>
            ),
          ]}
        />
      )}
      <MediaGallery
        variant="compact"
        urls={data.media_urls}
        captions={data.media_captions}
        className="mt-3"
      />
      <CardFooter
        isMinor={isMinor}
        status={
          <>
            {displayDate && !hideDate && (
              <time className={isMinor ? undefined : "text-text-secondary"}>
                {fmtDate(displayDate)}
              </time>
            )}
            {isPlanned && (
              <span className="rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary">
                Planned
              </span>
            )}
            {data.status === "draft" && !isPlanned && (
              <span className="rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary">
                Draft
              </span>
            )}
            {data.hidden && (
              <span className="rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
                Hidden
              </span>
            )}
            {data.last_edited_at && (
              <span title={`Last edited ${fmtDateTime(data.last_edited_at)}`}>
                updated {fmtRelative(data.last_edited_at)}
              </span>
            )}
          </>
        }
        actions={
          <>
            {onOpen && <DetailsButton onClick={onOpen} />}
            <CopyLinkButton url={releaseDrawerUrl(data.id)} />
            <CopyExportButtons composed={composeRelease(data)} />
            {canEditContent && (
              <Link
                href={`/releases/${data.id}`}
                className="rounded px-1.5 py-0.5 text-[11px] text-text-tertiary no-underline hover:text-text"
                title="Open the release editor"
              >
                Edit
              </Link>
            )}
          </>
        }
      />
    </CardShell>
  );
}

/** Full-page read-only release detail, shown by the Timeline. */
export function ReleaseDetailView({
  data,
  onBack,
}: {
  data: Release;
  onBack: () => void;
}) {
  const { canEditContent } = useEffectiveRole();
  const { builds, primary, isMinor, displayDate, titleText, platformShort, githubLinks } =
    releaseSummary(data);
  return (
    <DetailPageFrame
      title={titleText}
      meta={
        <>
          {platformShort && <span>{isMinor ? "Patch" : platformShort}</span>}
          {primary?.tag_name && (
            <span className="font-mono text-xs">{primary.tag_name}</span>
          )}
          {displayDate && <time>· {fmtDate(displayDate)}</time>}
        </>
      }
      actions={
        <>
          <CopyLinkButton url={releaseDrawerUrl(data.id)} />
          <CopyExportButtons composed={composeRelease(data)} />
          {canEditContent && <DetailEditLink href={`/releases/${data.id}`} />}
        </>
      }
      onBack={onBack}
    >
      {data.narrative && (
        <NarrativeBody text={data.narrative} className="text-text-secondary" />
      )}
      <LinksSection links={githubLinks} />
      <MediaGallery urls={data.media_urls} captions={data.media_captions} />
      {data.items && data.items.length > 0 && <ItemList items={data.items} />}
      <FooterMeta
        items={[
          primary?.author_login && !/\[bot\]$/.test(primary.author_login) && (
            <span key="author">By {primary.author_login}</span>
          ),
          builds.length > 1 && <span key="builds">{builds.length} builds</span>,
        ]}
      />
    </DetailPageFrame>
  );
}

/** Page shell for a detail view: back link, serif title, meta + actions,
 *  then the body in a readable measure. */
function DetailPageFrame({
  title,
  meta,
  actions,
  onBack,
  children,
}: {
  title: string;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <article className="mx-auto max-w-3xl animate-in fade-in-0 py-6 duration-200">
      <BackToTimeline onBack={onBack} />
      <header className="mt-4 border-b border-border pb-4">
        <h1 className="font-serif text-3xl leading-tight text-text">{title}</h1>
        {meta && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-text-secondary">
            {meta}
          </div>
        )}
        {actions && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px] text-text-tertiary">
            {actions}
          </div>
        )}
      </header>
      <div className="mt-5 space-y-5">{children}</div>
    </article>
  );
}

export function BackToTimeline({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 -ml-1.5 text-sm text-text-secondary hover:bg-border/50 hover:text-text"
      title="Back to the timeline (Esc)"
    >
      <span aria-hidden>←</span> Timeline
    </button>
  );
}

function DetailEditLink({ href }: { href: string }) {
  return (
    <Link
      href={href}
      className="rounded px-1.5 py-0.5 text-[11px] text-text-tertiary no-underline hover:text-text"
      title="Open the editor"
    >
      Edit
    </Link>
  );
}

/** Card container. Deliberately NOT interactive as a whole — opening the
 *  detail view is an explicit action (the clickable title + the footer
 *  "Details" button), so clicking anywhere else on the card never surprises
 *  the reader or steals a click meant for a thumbnail / control. */
function CardShell({
  isMinor,
  children,
}: {
  isMinor?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "group rounded-lg border transition-colors",
        isMinor
          ? "border-border/60 bg-card/60 px-4 py-2.5"
          : "border-border bg-card px-4 py-3 shadow-sm",
      )}
    >
      {children}
    </div>
  );
}

/** The card's serif headline, doubling as the primary affordance for opening
 *  the detail view. A real <button> (keyboard + screen-reader friendly);
 *  the dotted underline on hover/focus signals it's clickable. Plain text
 *  when there's nothing to open (static previews). */
function CardTitle({
  onClick,
  className,
  children,
}: {
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  if (!onClick) {
    return <div className={cn("font-serif", className)}>{children}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-sm text-left font-serif decoration-dotted underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
        className,
      )}
    >
      {children}
    </button>
  );
}

/** Explicit "open the detail" control for the footer action row — the
 *  discoverable, labeled counterpart to the clickable title. */
function DetailsButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-1.5 py-0.5 text-[11px] font-medium text-text-secondary hover:text-text"
      title="Open the full detail"
    >
      Details
    </button>
  );
}

/** Wrapper for the inline card controls (copy/export, hide, edit). Stops
 *  click + keyboard events from bubbling to the CardShell so acting on a
 *  control never also opens the drawer. */
function CardActions({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </span>
  );
}

/** Receded chrome row beneath the card's content. The availability answer
 *  (state pill) and the serif headline own the TOP of the card; author status
 *  (updated / draft / hidden / date) and the action cluster live here at the
 *  bottom, hairline-divided and muted, so the reading path is
 *  state → title → narrative → act rather than chrome-first. Copy leads the
 *  actions because lifting the copy is the consumer's primary job. */
function CardFooter({
  status,
  actions,
  isMinor,
}: {
  status?: React.ReactNode;
  actions: React.ReactNode;
  isMinor?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/60 text-[12.5px] text-text-tertiary",
        isMinor ? "mt-2 pt-2" : "mt-3 pt-2.5",
      )}
    >
      {status}
      <span className="flex-1" />
      <CardActions>{actions}</CardActions>
    </div>
  );
}

/** Timeline card for a published feature's announcement (EA or GA). The
 *  feed sets `announcement_state` per milestone; the displayed date is
 *  derived from ea_date/ga_date to match. Replaces the old EventCard,
 *  which read from the now-deleted flag_flip_events table. */
/** The single most-relevant date to surface on a feature card, labeled by
 *  milestone (EA / GA) and flagged `projected` when it's a coarse target
 *  (quarter / week / TBD) rather than a firm calendar date. Prefers the
 *  milestone matching the card's resolved state, falling back to the other
 *  date when that one is empty. Null when no date is set at all. */
function featureDatePlan(
  data: EnrichedFeature,
  resolvedKind: "in_development" | "ea" | "ga",
): { label: string; value: string; projected: boolean } | null {
  const forMilestone = (name: "EA" | "GA", raw: string | null) => {
    const pg = decodeProjectedGa(raw);
    if (pg.kind === "unset") return null;
    const value = formatProjectedGa(raw);
    if (!value) return null;
    const projected = pg.kind !== "date";
    return { label: projected ? `${name} target` : name, value, projected };
  };
  const ea = () => forMilestone("EA", data.ea_date);
  const ga = () => forMilestone("GA", data.ga_date);
  return resolvedKind === "ea" ? (ea() ?? ga()) : (ga() ?? ea());
}

interface FeatureAnnouncementCardProps {
  data: EnrichedFeature;
  /** Open the detail view. Omitted for static previews (the editor's live
   *  preview), which drops the Details affordances. */
  onOpen?: () => void;
}

/** Availability facts shared by the feature card and its detail panel. */
function useFeatureSummary(data: EnrichedFeature) {
  const { isSales } = useEffectiveRole();
  const { data: workspaces } = useWorkspaces();
  // Workspaces power the EA "N / total" count below. Until they load we show a
  // spinner instead of a bogus 0 / 0.
  const workspacesLoading = !workspaces;
  // Resolve the access tier: an explicit EA/GA pin wins; otherwise fall
  // back to the external-workspace tier derived from the primary flag
  // (which can be In-Development when no external customer has it yet).
  const tier = featureTier(data.flags, workspaces ?? []);
  const resolvedKind: "in_development" | "ea" | "ga" =
    data.announcement_state === "GA"
      ? "ga"
      : data.announcement_state === "EA"
        ? "ea"
        : tier.kind;
  const stateLabel =
    resolvedKind === "in_development"
      ? "Not yet available"
      : resolvedKind === "ga"
        ? "GA"
        : "EA";
  // The salient milestone date, labeled (EA / GA) and marked projected when
  // it's a coarse target. Forward projections are hidden from GTM/sales —
  // they don't preview unshipped plans; firm calendar dates always show.
  const datePlan = featureDatePlan(data, resolvedKind);
  const date =
    datePlan && !(datePlan.projected && isSales)
      ? { value: datePlan.value, projected: datePlan.projected }
      : undefined;
  // EA reach folded into the state pill: "N/M workspaces" (external
  // enabled / external total), or a small spinner until the list loads. Only
  // meaningful mid-rollout — GA is everyone, In-Dev is nobody — so undefined
  // for those (the chip then renders with no middle segment).
  const eaWorkspacesDetail: React.ReactNode =
    resolvedKind === "ea"
      ? workspacesLoading
        ? <Spinner size="sm" />
        : `${tier.externalEnabled}/${tier.externalTotal} workspaces`
      : undefined;
  // Headline shown inline under the title (customer-facing note first, else
  // the internal pitch).
  const headline = data.announcement_narrative || data.pitch || "";
  const stateChip = (
    <StateChip
      kind={resolvedKind}
      label={stateLabel}
      detail={eaWorkspacesDetail}
      date={date}
    />
  );
  return { headline, stateChip };
}

export function FeatureAnnouncementCard({
  data,
  onOpen,
}: FeatureAnnouncementCardProps) {
  const { canEditContent } = useEffectiveRole();
  const { headline, stateChip } = useFeatureSummary(data);
  return (
    <CardShell>
      {/* Signal row — the availability answer leads. State, its EA reach,
          and its milestone date read as one pill ("EA · 12/34 workspaces ·
          May 1") so the "who" and "when" are as scannable as the "what";
          hovering it reveals the workspace names. Readiness (Beta/Stable)
          sits beside it. Author chrome moves to the footer so the pill and
          the serif headline own the top of the card. */}
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <WorkspaceHoverWrap flags={data.flags} title={data.name}>
          {stateChip}
        </WorkspaceHoverWrap>
        <span
          className={
            data.beta
              ? "rounded-full bg-minor-soft px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-minor"
              : "rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary"
          }
        >
          {data.beta ? "Beta" : "Stable"}
        </span>
      </div>
      <CardTitle onClick={onOpen} className="mt-2 block text-2xl">
        {data.name}
      </CardTitle>
      {/* Headline stays visible on the card — the description is the point
          of the feed, not something to hunt for behind a click. */}
      {headline && (
        <NarrativeBody text={headline} className="mt-1.5 text-text-secondary" />
      )}
      {/* Links stay on the card face as real anchors; gaps and platform
          notes collapse to chips (hover for the text, click for detail). */}
      <CardLinksAndChips
        links={extractLinks(data.links_notes).filter(
          (l) => !headline.includes(l.href),
        )}
        chips={[
          data.known_gaps.length > 0 && (
            <InfoChip
              key="gaps"
              tone="warn"
              onClick={onOpen}
              title={data.known_gaps.map((g) => `• ${g}`).join("\n")}
            >
              {data.known_gaps.length} known{" "}
              {data.known_gaps.length === 1 ? "gap" : "gaps"}
            </InfoChip>
          ),
          data.web_note && (
            <InfoChip key="web" onClick={onOpen} title={data.web_note}>
              Web note
            </InfoChip>
          ),
          data.mobile_note && (
            <InfoChip key="mobile" onClick={onOpen} title={data.mobile_note}>
              Mobile note
            </InfoChip>
          ),
        ]}
      />
      <MediaGallery
        variant="compact"
        urls={data.media_urls}
        captions={data.media_captions}
        className="mt-3"
      />
      <CardFooter
        status={
          <>
            {data.last_edited_at && (
              <span title={`Last edited ${fmtDateTime(data.last_edited_at)}`}>
                updated {fmtRelative(data.last_edited_at)}
              </span>
            )}
            {data.status === "draft" && (
              <span className="rounded-full bg-border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-secondary">
                Draft
              </span>
            )}
          </>
        }
        actions={
          <>
            {onOpen && <DetailsButton onClick={onOpen} />}
            <CopyLinkButton url={featureDrawerUrl(data.id)} />
            <CopyExportButtons composed={composeFeature(data)} />
            {canEditContent && (
              <Link
                href={`/features/${data.id}`}
                className="rounded px-1.5 py-0.5 text-[11px] text-text-tertiary no-underline hover:text-text"
                title="Open the feature launch editor"
              >
                Edit
              </Link>
            )}
          </>
        }
      />
    </CardShell>
  );
}

/** Full-page read-only feature launch detail, shown by the Timeline. */
export function FeatureDetailView({
  data,
  onBack,
}: {
  data: EnrichedFeature;
  onBack: () => void;
}) {
  const { canEditContent } = useEffectiveRole();
  const { headline, stateChip } = useFeatureSummary(data);
  return (
    <DetailPageFrame
      title={data.name}
      meta={
        <>
          <WorkspaceHoverWrap flags={data.flags} title={data.name}>
            {stateChip}
          </WorkspaceHoverWrap>
          <span className={data.beta ? "text-minor" : "text-text-tertiary"}>
            {data.beta ? "Beta" : "Stable"}
          </span>
        </>
      }
      actions={
        <>
          <CopyLinkButton url={featureDrawerUrl(data.id)} />
          <CopyExportButtons composed={composeFeature(data)} />
          {canEditContent && <DetailEditLink href={`/features/${data.id}`} />}
        </>
      }
      onBack={onBack}
    >
      {headline && (
        <NarrativeBody text={headline} className="text-text-secondary" />
      )}
      {/* Pitch as secondary text only when we also had an authored
          narrative up top (otherwise the pitch already served as the
          headline). */}
      {data.announcement_narrative && data.pitch && (
        <NarrativeBody text={data.pitch} className="text-sm text-text-secondary" />
      )}

      <LinksSection links={extractLinks(data.links_notes)} />
      {/* The notes block only when there's prose beyond the URLs, which
          already show as pills above. */}
      {data.links_notes && notesHaveProse(data.links_notes) && (
        <div>
          <SectionLabel>Notes</SectionLabel>
          <NarrativeBody text={data.links_notes} className="text-sm text-text" />
        </div>
      )}

      {data.known_gaps.length > 0 && (
        <div>
          <SectionLabel>Known gaps</SectionLabel>
          <ul className="list-disc space-y-1 pl-6 text-sm text-text">
            {data.known_gaps.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      {data.web_note && <DetailRow label="Web">{data.web_note}</DetailRow>}
      {data.mobile_note && <DetailRow label="Mobile">{data.mobile_note}</DetailRow>}

      <MediaGallery urls={data.media_urls} captions={data.media_captions} />

      <EnabledWorkspacesLine flags={data.flags} />

      <FooterMeta
        items={[
          data.feature_family && (
            <span key="family">{data.feature_family}</span>
          ),
          data.owner_email && (
            <span key="owner">Owner {data.owner_email.split("@")[0]}</span>
          ),
          data.flag_ids.length > 0 && (
            <span key="flags" className="inline-flex flex-wrap items-center gap-1.5">
              {data.flag_ids.map((fid) => (
                <FlagChip key={fid} flagId={fid} />
              ))}
            </span>
          ),
        ]}
      />
    </DetailPageFrame>
  );
}

/** Labeled row of link pills for the detail pages, matching the card face. */
function LinksSection({ links }: { links: ExtractedLink[] }) {
  if (links.length === 0) return null;
  return (
    <div>
      <SectionLabel>Links</SectionLabel>
      <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            title={l.href}
            className="link-pill"
          >
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function notesHaveProse(text: string): boolean {
  const stripped = text
    .replace(/\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/https?:\/\/\S+/g, "");
  return /[A-Za-z0-9]/.test(stripped);
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
      {children}
    </div>
  );
}

/** One row under the card narrative: outbound links as labeled pills, then
 *  any summary chips. Renders nothing when both are empty. */
function CardLinksAndChips({
  links,
  chips,
}: {
  links: ExtractedLink[];
  chips?: React.ReactNode[];
}) {
  const presentChips = (chips ?? []).filter(Boolean);
  if (links.length === 0 && presentChips.length === 0) return null;
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[12px]">
      {links.map((l) => (
        <a
          key={l.href}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          title={l.href}
          className="link-pill"
        >
          {l.label}
        </a>
      ))}
      {presentChips}
    </div>
  );
}

/** Compact summary chip for content that lives in the detail view (known
 *  gaps, platform notes, change count). Hover shows the text; click opens
 *  the detail. */
function InfoChip({
  children,
  title,
  onClick,
  tone = "neutral",
}: {
  children: React.ReactNode;
  title: string;
  onClick?: () => void;
  tone?: "neutral" | "warn";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "rounded-full px-2 py-0.5 font-medium",
        tone === "warn"
          ? "bg-border text-text hover:bg-border-hover"
          : "bg-border/60 text-text-secondary hover:text-text",
      )}
    >
      {children}
    </button>
  );
}

/** Small labeled row for expanded announcement details — parity, projected
 *  GA, etc. Keeps the layout consistent with the read-only FeatureDetail. */
function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-sm">
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary">
        {label}
      </span>
      <span className="text-text">{children}</span>
    </div>
  );
}

function FooterMeta({ items }: { items: React.ReactNode[] }) {
  const present = items.filter(Boolean);
  if (present.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border pt-3 text-xs text-text-secondary">
      {present.map((n, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          {i > 0 && <span className="text-text-tertiary">·</span>}
          {n}
        </span>
      ))}
    </div>
  );
}

/** Editor-only "re-run the ingest" control inside the Uncurated drawer.
 *  Kicks off POST /api/staging/refresh (pulls the latest GitHub releases
 *  into the cache); on success the feed is invalidated so the list + count
 *  update in place. Viewers can't fetch, so they never see the button. */
function RefetchFromGithub() {
  const { isEditor } = useEffectiveRole();
  const refresh = useRefreshStaging();
  if (!isEditor) return null;
  const fetched = refresh.data?.results.reduce((n, r) => n + r.fetched, 0) ?? 0;
  const errored = refresh.data?.results.filter((r) => r.error) ?? [];
  return (
    <div className="mb-4 border-b border-border pb-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="rounded-md border border-border-hover bg-card px-3 py-1 text-sm text-text-secondary hover:border-text/40 hover:text-text disabled:opacity-60"
          title="Pull the latest GitHub releases into the cache"
        >
          {refresh.isPending ? "Re-fetching…" : "Re-fetch from GitHub"}
        </button>
        {refresh.isSuccess && errored.length === 0 && (
          <span className="text-xs text-text-tertiary">
            Fetched {fetched} release{fetched === 1 ? "" : "s"} — list updated.
          </span>
        )}
        {refresh.isError && (
          <span className="text-xs text-destructive">
            {refresh.error instanceof Error ? refresh.error.message : "Re-fetch failed"}
          </span>
        )}
      </div>
      {errored.length > 0 && (
        <p className="mt-2 text-xs text-destructive">
          {errored.map((r) => `${r.repo}: ${r.error}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

/** Toolbar affordance for the un-curated GitHub-release backlog: a button
 *  showing the count that opens a wide left drawer with a table of the raw
 *  builds. Lives off the main feed so nightlies/patches nobody wrote up don't
 *  flood the Timeline. Editors tick builds and turn a selection into ONE
 *  curated release (or ignore them in bulk); everyone else sees a read-only
 *  table. Read-only for everyone; editors also get a re-fetch button. */
export function UncuratedReleasesDrawer({ data }: { data: ReleaseRollup }) {
  return (
    <SideDrawer
      width="max-w-2xl"
      title="Uncurated releases"
      description={
        <>
          {data.count} GitHub release{data.count === 1 ? "" : "s"} since{" "}
          {fmtDate(data.since)} that no editor has curated or ignored yet.
        </>
      }
      trigger={
        <button
          type="button"
          className="rounded-md border border-border-hover bg-card px-3 py-1.5 text-sm text-text-secondary hover:border-text/40 hover:text-text"
          title={`Curate a release from ${data.count} uncurated GitHub build${data.count === 1 ? "" : "s"}`}
        >
          + New Release
        </button>
      }
    >
      <RefetchFromGithub />
      <UncuratedTable builds={data.builds} />
    </SideDrawer>
  );
}

/** The multi-select table inside the drawer. Editors tick rows and either
 *  "Create release" (converts the first pick — LLM-seeds narrative + items
 *  from the combined bodies — appends the rest, then jumps to the editor) or
 *  "Ignore" the selection. Non-editors get the same table without the
 *  checkboxes or action bar. Selection follows display order (newest first),
 *  so the newest tick seeds the release title/notes. */
function UncuratedTable({ builds }: { builds: ReleaseBuild[] }) {
  const { isEditor } = useEffectiveRole();
  const [, navigate] = useLocation();
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const create = useCreateReleaseFromBuilds();
  const ignore = useIgnoreBuilds();
  const busy = create.isPending || ignore.isPending;

  const allSelected = builds.length > 0 && selected.size === builds.length;
  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(builds.map((b) => b.github_release_id)));

  // Preserve display order so the newest pick is the convert seed.
  const picked = builds.filter((b) => selected.has(b.github_release_id));

  const onCreate = () =>
    create.mutate(picked, { onSuccess: (res) => navigate(`/releases/${res.id}`) });
  const onIgnore = () =>
    ignore.mutate(
      picked.map((b) => b.github_release_id),
      { onSuccess: () => setSelected(new Set()) },
    );

  // Creating a release LLM-seeds the narrative + items server-side, so it can
  // take a few seconds. Replace the table with a real centered spinner for
  // that wait instead of leaving a static "Creating…" button label.
  if (create.isPending) {
    return (
      <Loading
        label={
          picked.length > 1
            ? `Creating release from ${picked.length} builds`
            : "Creating release"
        }
      />
    );
  }

  return (
    <>
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wider text-text-tertiary">
            {isEditor && (
              <th className="w-8 py-2">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-accent"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
            )}
            <th className="py-2 pr-3 font-semibold">Platform</th>
            <th className="py-2 pr-3 font-semibold">Version</th>
            <th className="py-2 pr-3 font-semibold">Published</th>
            <th className="py-2 font-semibold" />
          </tr>
        </thead>
        <tbody>
          {builds.map((b) => {
            const checked = selected.has(b.github_release_id);
            return (
              <tr
                key={b.github_release_id}
                className={
                  checked
                    ? "border-b border-border/60 bg-accent/5"
                    : "border-b border-border/60"
                }
              >
                {isEditor && (
                  <td className="py-1.5 align-top">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-accent"
                      checked={checked}
                      onChange={() => toggle(b.github_release_id)}
                      aria-label={`Select ${b.tag_name}`}
                    />
                  </td>
                )}
                <td className="py-1.5 pr-3 align-top font-mono text-[11px] text-text-tertiary">
                  {platformLabel(b)}
                </td>
                <td className="py-1.5 pr-3 align-top font-mono text-xs text-text">
                  <span className="break-all">{b.tag_name}</span>
                </td>
                <td className="whitespace-nowrap py-1.5 pr-3 align-top text-text-tertiary">
                  {fmtDate(b.published_at)}
                </td>
                <td className="py-1.5 align-top">
                  {b.html_url && (
                    <a
                      href={b.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      GitHub
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isEditor && (
        <div className="sticky bottom-0 -mx-6 mt-1 flex flex-wrap items-center gap-3 border-t border-border bg-card px-6 py-3">
          <span className="text-sm text-text-secondary">
            {picked.length === 0
              ? "Select builds to curate"
              : `${picked.length} selected`}
          </span>
          {(create.error || ignore.error) && (
            <span className="text-xs text-destructive">
              {(create.error ?? ignore.error)?.message ?? "Action failed"}
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            onClick={onIgnore}
            disabled={busy || picked.length === 0}
            className="rounded-md border border-border-hover bg-card px-3 py-1 text-sm text-text-tertiary hover:border-text/60 hover:text-text disabled:opacity-40"
            title="Hide the selected builds from the backlog"
          >
            {ignore.isPending ? "Ignoring…" : "Ignore"}
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={busy || picked.length === 0}
            className="rounded-md border border-text/30 bg-text px-3 py-1 text-sm text-bg hover:bg-text/90 disabled:opacity-40"
            title="Create one draft release wrapping the selected builds, then open its editor"
          >
            {create.isPending
              ? "Creating…"
              : picked.length > 1
                ? `Create release (${picked.length})`
                : "Create release"}
          </button>
        </div>
      )}
    </>
  );
}
